import { AgentSearchService } from 'ag-common-svc/public-api';
import { computed, Injectable, signal } from '@angular/core';
import {
  Agent,
  AgentKeys,
  AttendeeKeys,
  Conference,
  ConferenceKeys,
  ConferencePossibleStayPeriodKey,
  ConferenceRegistrationStepName,
  ConferenceStepsConfigurationKeys,
  ConferenceGuestsStepsConfigurationSectionName,
  GuestData,
  HotelReservationKeys,
  PhoneNumberKeys,
  Registrant,
  RegistrantData,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrantWizardStateKeys,
  RegistrationType,
  WizardStepState,
  Entity,
  EmailAddressKeys,
  EntityPermissionActivityKeys,
  ChangeSourceType,
  InviteeStatus,
  HotelReservation,
} from 'ag-common-lib/public-api';
import {
  AgentEmailAddressesService,
  BaseFormService,
  ConferenceGuestsService,
  ConferenceRegistrantsService,
  ConferenceService,
  getCurrentStepInProgress,
  prepareStepsToComplete,
} from 'ag-common-svc/public-api';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  from,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subject,
  take,
  tap,
} from 'rxjs';
import {
  ATTENDEE_DETAIL_MODAL_SECTIONS_CONFIGURATION,
  AttendeeDetailsModalSection,
  AttendeeSectionGroups,
  AttendeeSectionGroupsData,
  attendeeSectionGroupsToFieldsMap,
} from './attendee-details-modal.model';
import { AgentAssociationsService } from 'ag-common-svc/lib/services/agent-associations.service';
import { cloneDeep, isEqual, merge, set } from 'lodash';
import { getAddressKeyExpression } from 'ag-common-svc/lib/utils/address.util';
import { SuffixPipe } from 'ag-common-svc/shared/pipes/suffix.pipe';
import { HasPermissionPipe } from 'ag-common-svc/shared/pipes/has-permission.pipe';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { RegistrationSummaryEmailService } from 'ag-common-svc/lib/services/registration-summary-email/registration-summary-email.service';
import { IsRequestedDifferentHotelCheckInCheckOutDatesPipe } from 'ag-common-svc/shared/pipes/is-requested-different-hotel-check-in-check-out-dates.pipe';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ChairmanExtensionConfigurationKeys } from 'ag-common-lib/lib/models/registration/conference-chairman-extension';
import {
  RegistrantChairmanExtension,
  RegistrantChairmanExtensionKeys,
} from 'ag-common-lib/lib/models/registration/registrant-chairman-extension';
import onChange from 'on-change';

@Injectable()
export class AttendeeDetailsModalService extends BaseFormService<RegistrantData> {
  userAction$ = new Subject<any>();

  private _isRequestedDifferentHotelCheckInCheckOutDates$ = new BehaviorSubject(false);
  isRequestedDifferentHotelCheckInCheckOutDates$ = this._isRequestedDifferentHotelCheckInCheckOutDates$.asObservable();

  assignOwnerList$: Observable<DataSource<any, any>>;
  attendee$ = new BehaviorSubject<Attendee>(null);
  private _sectionsInEditState$ = new BehaviorSubject(new Set<AttendeeSectionGroups>());
  sectionsInEditState$ = this._sectionsInEditState$.asObservable();

  private _sectionsEditMapState = new Map<AttendeeSectionGroups, AttendeeSectionGroupsData<AttendeeSectionGroups>>();

  isBillingDifferentFromShippingAddressSig = computed(() => {
    const registrantData = this.formDataSig();
    const isBillingDifferentFromShippingAddress =
      registrantData?.[RegistrantKeys.isBillingDifferentFromShippingAddress];

    return isBillingDifferentFromShippingAddress;
  });

  isFirstTimeAttendeeSig = computed(() => {
    const registrantData = this.formDataSig();
    const isFirstTimeAttendee = registrantData?.[RegistrantKeys.firstTimeAttendee];

    return isFirstTimeAttendee;
  });
  isFirstTimeAttendee$ = toObservable(this.isFirstTimeAttendeeSig);

  private _conferenceDbId$ = new BehaviorSubject<string>(null);
  conferenceDbId$ = this._conferenceDbId$.asObservable();
  conferenceDbIdSig = toSignal(this._conferenceDbId$);

  conference$: Observable<Conference> = this._conferenceDbId$.pipe(
    filter(Boolean),
    switchMap(conferenceDbId => {
      return this.conferenceService.getDocumentData(conferenceDbId);
    }),
    shareReplay(1),
  );
  conferenceSig = toSignal(this.conference$);

  private _registrantDbId$ = new BehaviorSubject<string>(null);
  registrantDbId$ = this._registrantDbId$.asObservable();
  registrantDbIdSig = toSignal(this._registrantDbId$);

  registrantIds$: Observable<{ registrantDbId: string; conferenceDbId: string }> = combineLatest({
    registrantDbId: this._registrantDbId$.pipe(filter(Boolean)),
    conferenceDbId: this._conferenceDbId$.pipe(filter(Boolean)),
  }).pipe(distinctUntilChanged(isEqual), shareReplay(1));

  // switchMap (not mergeMap): reopening the modal for another registrant must tear down the
  // previous registrant's document listener, otherwise it keeps emitting over the current one.
  registrant$: Observable<Registrant> = this.registrantIds$.pipe(
    switchMap(({ conferenceDbId, registrantDbId }) => {
      return this.conferenceRegistrantsService.getDocumentData(conferenceDbId, registrantDbId);
    }),
    shareReplay(1),
  );
  registrantSig = toSignal(this.registrant$);

  associationsStateSig = signal<{ isLoaded: boolean; inProgress: boolean }>({ isLoaded: false, inProgress: false });
  protected agentAssociations$ = this.registrantDbId$.pipe(
    filter(Boolean),
    tap(() => {
      this.associationsStateSig.set({ isLoaded: false, inProgress: true });
    }),
    mergeMap((registrantDbId: string) => {
      return this.agentAssociationsService.getList(registrantDbId);
    }),
    catchError(() => {
      return [];
    }),
    tap(() => {
      this.associationsStateSig.set({ isLoaded: true, inProgress: false });
    }),
  );

  agentAssociationsSig = toSignal(this.agentAssociations$);

  isApplicableForChairmanExtensionSig = computed(() => {
    const registrant = this.registrantSig();
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const chairmanExtension = registrantData?.[RegistrantKeys.chairmanExtension];
    const isApplicableForChairmanExtension = registrantData?.[RegistrantKeys.isApplicableForChairmanExtension];

    return isApplicableForChairmanExtension;
  });

  isAcceptedChairmanExtensionSig = computed(() => {
    const registrant = this.registrantSig();
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const chairmanExtension = registrantData?.[RegistrantKeys.chairmanExtension];
    const chairmanExtensionInviteeStatus = chairmanExtension?.[RegistrantChairmanExtensionKeys.inviteeStatus];
    const isAcceptedChairmanExtension = chairmanExtensionInviteeStatus === InviteeStatus.accepted;

    return isAcceptedChairmanExtension;
  });

  wizardState$ = this.registrant$.pipe(map(registrant => registrant?.[RegistrantModelKeys.wizardState]));

  conferenceStepsConfiguration$ = this.conference$.pipe(
    map(conference => conference?.[ConferenceKeys.stepsConfiguration]),
  );
  conferenceGuestExcludedSections$ = this.conferenceStepsConfiguration$.pipe(
    map(config => {
      return config.find(
        c =>
          c[ConferenceStepsConfigurationKeys.stepName] ===
          ConferenceRegistrationStepName.registrantGuestConfigurationStep,
      )?.[ConferenceStepsConfigurationKeys.excludedSections] as ConferenceGuestsStepsConfigurationSectionName[];
    }),
  );

  conferenceGuestExcludedFields$ = this.conferenceStepsConfiguration$.pipe(
    map(config => {
      return config.find(
        c =>
          c[ConferenceStepsConfigurationKeys.stepName] ===
          ConferenceRegistrationStepName.registrantGuestConfigurationStep,
      )?.[ConferenceStepsConfigurationKeys.excludedFields] as RegistrantKeys[];
    }),
  );
  conferencePersonalInfoExcludedSections$ = this.conferenceStepsConfiguration$.pipe(
    map(config => {
      return config.find(
        c =>
          c[ConferenceStepsConfigurationKeys.stepName] ===
          ConferenceRegistrationStepName.registrantPersonalInformationStep,
      )?.[ConferenceStepsConfigurationKeys.excludedSections] as ConferenceRegistrationStepName[];
    }),
  );

  conferencePersonalInfoExcludedFields$ = this.conferenceStepsConfiguration$.pipe(
    map(config => {
      return config.find(
        c =>
          c[ConferenceStepsConfigurationKeys.stepName] ===
          ConferenceRegistrationStepName.registrantPersonalInformationStep,
      )?.[ConferenceStepsConfigurationKeys.excludedFields] as RegistrantKeys[];
    }),
  );

  eventName$ = this.conference$.pipe(map(conference => conference?.[ConferenceKeys.eventName]));

  private agent$: Observable<Agent> = this._registrantDbId$.pipe(
    filter(Boolean),
    mergeMap(agentDbId => {
      return from(this.agentSearchService.getById(agentDbId));
    }),
    map(agent => agent ?? null),
    shareReplay(1),
  );

  agentSig = toSignal(this.agent$);

  private agentEmailAddresses$ = this._registrantDbId$.pipe(
    filter(Boolean),
    mergeMap((registrantDbId: string) => this.agentEmailAddressesService.getList(registrantDbId)),
    shareReplay(1),
  );

  agentEmailAddressesSig = toSignal(this.agentEmailAddresses$);

  private agentEmergencyContacts$ = this._registrantDbId$.pipe(
    filter(Boolean),
    mergeMap((registrantDbId: string) => this.agentAssociationsService.getEmergencyContactsList(registrantDbId)),
    shareReplay(1),
  );

  agentEmergencyContactsSig = toSignal(this.agentEmergencyContacts$);

  agentPhoneNumbersSig = computed(() => {
    const agent = this.agentSig();
    const phoneNumbers = agent?.[AgentKeys.phone_numbers];

    return phoneNumbers;
  });

  agentAddressesSig = computed(() => {
    const agent = this.agentSig();
    const addresses = agent?.[AgentKeys.addresses];

    return addresses;
  });

  attendeeSections$ = combineLatest({
    conference: this.conference$.pipe(filter(Boolean)),
    hasLogsPermission: this.hasPermissionPipe
      .transform(Entity.conferenceChangeLogs, EntityPermissionActivityKeys.read)
      .pipe(shareReplay(1)),
    hasTransactionHistoryPermission: this.hasPermissionPipe
      .transform(Entity.conferenceTransactions)
      .pipe(shareReplay(1)),
  }).pipe(
    map(({ conference, hasLogsPermission, hasTransactionHistoryPermission }) => {
      const sectionsMap = new Map<AttendeeDetailsModalSection, boolean>([
        [AttendeeDetailsModalSection.flyInformation, conference?.[ConferenceKeys.flightsEnabled] ?? false],
        [AttendeeDetailsModalSection.hotelReservation, conference?.[ConferenceKeys.hotelEnabled] ?? false],
        [AttendeeDetailsModalSection.guests, conference?.[ConferenceKeys.guestsEnabled] ?? false],
        [AttendeeDetailsModalSection.excursions, conference?.[ConferenceKeys.excursionsEnabled] ?? false],
        [AttendeeDetailsModalSection.excursions, conference?.[ConferenceKeys.excursionsEnabled] ?? false],
        [AttendeeDetailsModalSection.changeLog, hasLogsPermission ?? false],
        [AttendeeDetailsModalSection.transactions, hasTransactionHistoryPermission ?? false],
      ]);

      return ATTENDEE_DETAIL_MODAL_SECTIONS_CONFIGURATION.filter(section => {
        if (sectionsMap.has(section?.id)) {
          return sectionsMap.get(section?.id);
        }
        return true;
      });
    }),
    shareReplay(1),
  );

  // Stamped with the registrant the guests belong to. Consumers that need to know *whose* guests
  // they received (see showModal$) cannot tell from the array alone, and shareReplay(1) hands a new
  // subscriber the previous registrant's guests until the new fetch resolves.
  //
  // Keyed off registrantIds$, NOT registrant$: getList is a live onSnapshot listener, so it keeps
  // the guests current on its own. Keying it off the registrant document would tear down and
  // re-create that listener on every write to the registrant, re-emitting the whole flight data
  // source and reloading the grids underneath the user.
  registrantGuestsWithRegistrantDbId$: Observable<{ registrantDbId: string; guests: GuestData[] }> =
    this.registrantIds$.pipe(
      switchMap(({ conferenceDbId, registrantDbId }) => {
        return this.conferenceGuestsService
          .getList(conferenceDbId, registrantDbId)
          .pipe(map(guests => ({ registrantDbId, guests })));
      }),
      shareReplay(1),
    );

  registrantGuests$ = this.registrantGuestsWithRegistrantDbId$.pipe(map(({ guests }) => guests));

  private _guestsIds$ = this.registrantGuests$.pipe(map(guests => guests.map(guest => guest[BaseModelKeys.dbId])));

  currentStepInProgress$ = combineLatest({
    conference: this.conference$,
    conferenceRegistration: this.registrant$,
    guestsIds: this._guestsIds$,
  }).pipe(map(getCurrentStepInProgress), shareReplay(1));

  mayBeRefreshFromAgent$ = combineLatest({
    agent: this.agent$,
    conferenceRegistration: this.registrant$,
  }).pipe(
    switchMap(({ agent, conferenceRegistration }) =>
      from(Promise.all([firstValueFrom(this.suffixPipe.transform(agent?.[AgentKeys.p_suffix]))])).pipe(
        map(([suffix]) => ({ suffix, agent, conferenceRegistration })),
      ),
    ),
    map(({ suffix, agent, conferenceRegistration }) => {
      const wizardState = conferenceRegistration?.[RegistrantModelKeys.wizardState] ?? {};

      if (
        wizardState?.[ConferenceRegistrationStepName.registrantPersonalInformationAboutStep] &&
        wizardState?.[ConferenceRegistrationStepName.registrantPersonalInformationAboutStep] !== WizardStepState.initial
      ) {
        return false;
      }

      const conferenceRegistrationInfo = conferenceRegistration?.[RegistrantModelKeys.data];

      const agentPrefixId = agent?.[AgentKeys.p_prefix];
      const agentSuffixId = agent?.[AgentKeys.p_suffix];
      const agentFirstName = agent?.[AgentKeys.p_agent_first_name];
      const agentLastName = agent?.[AgentKeys.p_agent_last_name];
      const agentBadgeName = [agentFirstName, agentLastName, suffix].filter(Boolean).join(' ');
      const phoneNumbers = agent?.[AgentKeys.phone_numbers];

      const agentPrimaryPhoneNumber = phoneNumbers?.find(phoneNumber => phoneNumber?.[PhoneNumberKeys.is_primary]);

      const agentInfo = {
        [RegistrantKeys.prefix]: agentPrefixId,
        [RegistrantKeys.firstName]: agentFirstName,
        [RegistrantKeys.middleName]: agent?.[AgentKeys.p_agent_middle_name],
        [RegistrantKeys.lastName]: agentLastName,
        [RegistrantKeys.suffix]: agentSuffixId,
        [RegistrantKeys.gender]: agent?.[AgentKeys.gender],
        [RegistrantKeys.dob]: agent?.[AgentKeys.dob],
        [RegistrantKeys.badgeName]: agentBadgeName,
        [RegistrantKeys.mobilePhone]: agentPrimaryPhoneNumber,
      };

      const keysToCheck = [
        RegistrantKeys.prefix,
        RegistrantKeys.firstName,
        RegistrantKeys.middleName,
        RegistrantKeys.lastName,
        RegistrantKeys.suffix,
        RegistrantKeys.gender,
        RegistrantKeys.dob,
        RegistrantKeys.badgeName,
      ];
      const agentPrimaryInfoNotEqual = keysToCheck.some(key => {
        console.log(conferenceRegistrationInfo[key], agentInfo[key]);

        return conferenceRegistrationInfo[key] !== agentInfo[key];
      });

      const phoneNumbersNotEqual =
        conferenceRegistrationInfo[RegistrantKeys.mobilePhone][PhoneNumberKeys.number] !==
        agentInfo[RegistrantKeys.mobilePhone][PhoneNumberKeys.number];

      return agentPrimaryInfoNotEqual || phoneNumbersNotEqual;
    }),
    shareReplay(1),
  );

  private _showModal$ = new Subject<string>();
  showModal$: Observable<number> = this._showModal$.pipe(
    filter(Boolean),
    tap(() => {
      this.startLoadData();
    }),
    switchMap(attendeeDbDbId => {
      return this.registrant$.pipe(
        filter(Boolean),
        map(registrant => registrant?.[BaseModelKeys.dbId]),
        filter(registrantDbId => registrantDbId === attendeeDbDbId),
        take(1),
        // Tabs assembled from registrant + guests (Flight Information) would otherwise paint the
        // previous registrant's guests once the registrant lands but before the guests do. Hold the
        // load panel until the guests stamped with *this* registrant have arrived.
        switchMap(registrantDbId =>
          this.registrantGuestsWithRegistrantDbId$.pipe(
            filter(guestsData => guestsData?.registrantDbId === registrantDbId),
            take(1),
          ),
        ),
      );
    }),
    switchMap(() =>
      this.attendeeSections$.pipe(
        map(sections => {
          const index = sections?.findIndex(section => section?.id === this._defaultSectionId);

          return index < 0 ? 0 : index;
        }),
        take(1),
      ),
    ),
    tap(() => {
      this.stopLoadData();
    }),
    finalize(() => {
      this.stopLoadData();
    }),
    shareReplay(1),
  );

  private _defaultSectionId: AttendeeDetailsModalSection;
  private _inviteeEmail: string;
  private _sourceType = ChangeSourceType.regularArm;

  constructor(
    private suffixPipe: SuffixPipe,
    private toastrService: ToastrService,
    private agentSearchService: AgentSearchService,
    private agentAssociationsService: AgentAssociationsService,
    private conferenceService: ConferenceService,
    private conferenceGuestsService: ConferenceGuestsService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
    private agentEmailAddressesService: AgentEmailAddressesService,
    private registrationSummaryEmailService: RegistrationSummaryEmailService,
    private isRequestedDifferentHotelCheckInCheckOutDatesPipe: IsRequestedDifferentHotelCheckInCheckOutDatesPipe,
    private hasPermissionPipe: HasPermissionPipe,
  ) {
    super();

    combineLatest({ conference: this.conference$, registrant: this.registrant$ }).subscribe(this.getFormData);

    this.assignOwnerList$ = this.conference$.pipe(
      map(conference => conference?.[ConferenceKeys.permissionConfigurationIds]),
      mergeMap(permissionConfigurationIds => {
        return from(this.agentSearchService.getByIds(permissionConfigurationIds));
      }),
      map(assignOwnerList => {
        assignOwnerList = assignOwnerList.map(item => ({
          ...item,
          [AgentKeys.email_addresses]: item[AgentKeys.email_addresses] || [
            {
              [EmailAddressKeys.address]: item[AgentKeys.p_email],
              [EmailAddressKeys.isLogin]: true,
            },
          ],
        }));
        return new DataSource({
          store: new ArrayStore({
            key: 'dbId',
            data: Array.isArray(assignOwnerList) ? assignOwnerList : [],
          }),
        });
      }),
      shareReplay(1),
    );
  }

  showModal = (
    attendee: Attendee,
    defaultSectionId: AttendeeDetailsModalSection = AttendeeDetailsModalSection.generalInfo,
  ): Promise<any> => {
    this.attendee$.next(attendee); // to send email
    this._inviteeEmail = attendee?.[AttendeeKeys.inviteeEmail];
    const conferenceDbId = attendee?.[AttendeeKeys.conferenceDbId];
    this._defaultSectionId = defaultSectionId;
    this._conferenceDbId$.next(conferenceDbId);
    const registrantDbId = attendee.registrant?.[BaseModelKeys.dbId];
    this._registrantDbId$.next(attendee.registrant?.[BaseModelKeys.dbId]);
    this._showModal$.next(registrantDbId);

    return firstValueFrom(this.userAction$);
  };

  setRegistrationData = (attendee?: Attendee) => {
    const conferenceDbId = attendee?.[AttendeeKeys.conferenceDbId];
    this._conferenceDbId$.next(conferenceDbId);
    this._registrantDbId$.next(attendee?.registrant?.[BaseModelKeys.dbId]);
  };

  getFormData = async ({
    registrant,
    conference,
  }: {
    registrant?: Registrant;
    conference: Conference;
  }): Promise<RegistrantData> => {
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const wizardState = registrant?.[RegistrantModelKeys.wizardState];

    this.calculateIsRequestedDifferentHotelDates(registrantData);

    const shippingAddress = registrantData?.[RegistrantKeys.shippingAddress];
    const billingAddress = registrantData?.[RegistrantKeys.billingAddress];
    const shippingAddressKey = getAddressKeyExpression(shippingAddress);
    const billingAddressKey = getAddressKeyExpression(billingAddress);
    const isBillingDifferentFromShippingAddress = shippingAddressKey !== billingAddressKey;
    const initialData = cloneDeep(
      Object.assign({}, registrantData, {
        [RegistrantKeys.isBillingDifferentFromShippingAddress]: isBillingDifferentFromShippingAddress,
      }),
    );
    const formData = onChange(initialData, (path, value, prevValue) => {
      this.formChangesDetector.handleChange(path, value, prevValue);
      switch (path) {
        case RegistrantKeys.isApplicableForChairmanExtension:
          value
            ? Object.assign(formData, {
                [RegistrantKeys.chairmanExtension]: Object.assign({}, new RegistrantChairmanExtension()),
              })
            : Object.assign(formData, {
                [RegistrantKeys.chairmanExtension]: null,
              });
          break;
        case RegistrantKeys.registrationType:
          this.getTravelDates(
            conference,
            wizardState,
            value as RegistrationType,
            formData?.[RegistrantKeys.isApplicableForChairmanExtension],
          );
          break;
        case RegistrantKeys.isApplicableForChairmanExtension:
          this.getTravelDates(conference, wizardState, formData?.[RegistrantKeys.registrationType], value as boolean);
          break;

        case RegistrantKeys.inviteeStatus:
          Object.assign(formData, { [RegistrantKeys.inviteeOutcomeStatus]: null });
          break;

        case RegistrantKeys.isBillingDifferentFromShippingAddress:
          if (!value) {
            Object.keys(formData?.[RegistrantKeys.billingAddress]).forEach(key => {
              set(formData?.[RegistrantKeys.billingAddress], key, null);
            });
            Object.entries(formData?.[RegistrantKeys.shippingAddress]).forEach(([key, value]) => {
              set(formData?.[RegistrantKeys.billingAddress], key, value);
            });
          }
          break;
        default:
          break;
      }

      if (
        path?.startsWith(`${RegistrantKeys.shippingAddress}.`) &&
        !formData?.[RegistrantKeys.isBillingDifferentFromShippingAddress]
      ) {
        const prop = path?.replace(`${RegistrantKeys.shippingAddress}.`, '');
        console.log('set billing address ', prop, ' to: ', value);

        set(formData[RegistrantKeys.billingAddress], prop, value);
      }
    });

    this.formDataSig.set(formData);
    this.formChangesDetector.clear();
    this.stopLoadData();

    return formData;
  };

  updateRegistrant = async (): Promise<void> => {
    if (this._inviteeEmail !== this.formDataSig()?.[RegistrantKeys.inviteeEmail]) {
      this.toastrService.error("Data can't saved. Reload required.");
      return;
    }

    const conferenceDbId = this.conferenceDbIdSig();
    const registrantDbId = this.registrantDbIdSig();

    this.startProgress();
    const registrantData = onChange.target(this.formDataSig());
    return this.conferenceRegistrantsService
      .update(conferenceDbId, registrantDbId, { [RegistrantModelKeys.data]: registrantData }, this._sourceType, true)
      .then(() => {
        this.toastrService.success('Attendee Information Successfully Updated');
        this.formChangesDetector.clear();
      })
      .catch(err => {
        this.toastrService.error('Attendee Information was Not Updated');
        throw err;
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  lockWizard = async (): Promise<void> => {
    const conferenceRegistration = await firstValueFrom(this.registrant$);
    const conference = await firstValueFrom(this.conference$);
    this.startProgress();

    const wizardState = prepareStepsToComplete({
      conference,

      conferenceRegistration,
    });

    Object.assign(wizardState, {
      [RegistrantWizardStateKeys.isSubmitted]: true,
    });

    return this.conferenceRegistrantsService
      .update(
        conferenceRegistration?.[RegistrantModelKeys.conferenceDbId],
        conferenceRegistration?.[BaseModelKeys.dbId],
        {
          [RegistrantModelKeys.wizardState]: wizardState,
        },
        this._sourceType,
        true,
      )
      .then(() => {
        this.revertAllChanges();
      })
      .catch(err => {
        throw err;
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  unlockWizard = async (): Promise<void> => {
    const conferenceDbId = this.conferenceDbIdSig();
    const registrantDbId = this.registrantDbIdSig();
    this.startProgress();

    // TODO handle agent data changes

    return this.conferenceRegistrantsService
      .update(
        conferenceDbId,
        registrantDbId,
        {
          [RegistrantModelKeys.wizardState]: {
            [RegistrantWizardStateKeys.isSubmitted]: false,
            [ConferenceRegistrationStepName.registrationSummaryStep]: null,
            [ConferenceRegistrationStepName.registrantPaymentStep]: null,
            [ConferenceRegistrationStepName.registrationConfirmationStep]: null,
          },
        },
        this._sourceType,
        true,
      )
      .then(() => {
        this.revertAllChanges();
      })
      .catch(err => {
        throw err;
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  updateAttendee = async (): Promise<void> => {
    const agent = this.agentSig();
    const conferenceDbId = this.conferenceDbIdSig();
    const registrantDbId = this.registrantDbIdSig();
    this.startProgress();

    const agentPrefixId = agent?.[AgentKeys.p_prefix];
    const agentSuffixId = agent?.[AgentKeys.p_suffix];
    const agentFirstName = agent?.[AgentKeys.p_agent_first_name];
    const agentLastName = agent?.[AgentKeys.p_agent_last_name];
    const [suffix] = await Promise.all([firstValueFrom(this.suffixPipe.transform(agentSuffixId))]);
    const agentBadgeName = [agentFirstName, agentLastName, suffix].filter(Boolean).join(' ');
    const phoneNumbers = agent?.[AgentKeys.phone_numbers];

    const agentPrimaryPhoneNumber = phoneNumbers?.find(phoneNumber => phoneNumber?.[PhoneNumberKeys.is_primary]);

    const agentInfo = {
      [RegistrantKeys.prefix]: agentPrefixId,
      [RegistrantKeys.firstName]: agentFirstName,
      [RegistrantKeys.middleName]: agent?.[AgentKeys.p_agent_middle_name],
      [RegistrantKeys.lastName]: agentLastName,
      [RegistrantKeys.suffix]: agentSuffixId,
      [RegistrantKeys.gender]: agent?.[AgentKeys.gender],
      [RegistrantKeys.dob]: agent?.[AgentKeys.dob],
      [RegistrantKeys.badgeName]: agentBadgeName,
      [RegistrantKeys.mobilePhone]: agentPrimaryPhoneNumber,
    };

    return this.conferenceRegistrantsService
      .update(
        conferenceDbId,
        registrantDbId,
        {
          [RegistrantModelKeys.data]: agentInfo,
        },
        this._sourceType,
        true,
      )
      .then(() => {
        this.revertAllChanges();
      })
      .catch(err => {
        throw err;
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  startEditSectionGroup = (sectionGroupId: AttendeeSectionGroups) => {
    const sectionsInEditState = this._sectionsInEditState$.value;
    sectionsInEditState.add(sectionGroupId);
    this._sectionsEditMapState.set(
      sectionGroupId,
      Object.assign({}, this.formDataSig()[attendeeSectionGroupsToFieldsMap.get(sectionGroupId)]),
    );
  };

  cancelEditSectionGroup = (sectionGroupId: AttendeeSectionGroups) => {
    const sectionsInEditState = this._sectionsInEditState$.value;
    sectionsInEditState.delete(sectionGroupId);
    this.formDataSig()[attendeeSectionGroupsToFieldsMap.get(sectionGroupId)] = {
      ...this._sectionsEditMapState.get(sectionGroupId),
    };
    this._sectionsEditMapState.delete(sectionGroupId);
  };

  saveEditSectionGroup = (sectionGroupId: AttendeeSectionGroups) => {
    const sectionsInEditState = this._sectionsInEditState$.value;
    sectionsInEditState.delete(sectionGroupId);
    this._sectionsEditMapState.delete(sectionGroupId);
  };

  private calculateIsRequestedDifferentHotelDates = (registrant: RegistrantData) => {
    const hotelReservation = registrant?.[RegistrantKeys.hotelReservation];
    const checkInDate = hotelReservation?.[HotelReservationKeys.checkInDate];
    const checkOutDate = hotelReservation?.[HotelReservationKeys.checkOutDate];
    const requestedCheckInDate = hotelReservation?.[HotelReservationKeys.requestedCheckInDate];
    const requestedCheckOutDate = hotelReservation?.[HotelReservationKeys.requestedCheckOutDate];

    const isRequestedDifferentHotelCheckInCheckOutDates =
      this.isRequestedDifferentHotelCheckInCheckOutDatesPipe.transform(
        checkInDate,
        checkOutDate,
        requestedCheckInDate,
        requestedCheckOutDate,
      );

    this._isRequestedDifferentHotelCheckInCheckOutDates$.next(isRequestedDifferentHotelCheckInCheckOutDates);
  };

  private getTravelDates = (
    conference: Conference,
    wizardState,
    registrationType: RegistrationType,
    isApplicableForChairmanExtension: boolean,
  ) => {
    if (!wizardState?.[ConferenceRegistrationStepName.registrantHotelReservationStep]) {
      const hotelReservation = Attendee.getRegistrantTravelDates(
        conference,
        registrationType,
        isApplicableForChairmanExtension,
      );
      merge(this.formDataSig(), hotelReservation);
    }
  };

  getExcludedFields = (
    stepNames: ConferenceRegistrationStepName | ConferenceRegistrationStepName[],
  ): Observable<RegistrantKeys[]> => {
    if (!stepNames?.length) {
      return of([]);
    }

    const stepNamesToCheck = Array.isArray(stepNames) ? stepNames : [stepNames];

    return this.conferenceStepsConfiguration$.pipe(
      take(1),
      map(configuration => {
        return configuration
          .filter(config => stepNamesToCheck.includes(config.stepName))
          .flatMap(config => config?.[ConferenceStepsConfigurationKeys.excludedFields] ?? []);
      }),
    );
  };
}
